<template>
  <div class="board-wrap">
    <div
      class="game-board"
      :style="gridStyle"
      :class="[`theme--${theme}`, { 'show-grid': showGrid }]"
      role="application"
      aria-label="Snake game board"
    >
      <!-- Obstacles -->
      <div
        v-for="ob in obstacles"
        :key="`obs-${ob.x}-${ob.y}`"
        class="cell obstacle"
        :style="cellStyle(ob.x, ob.y)"
        aria-hidden="true"
      />

      <!-- Food -->
      <div
        v-if="foodPosition"
        class="cell food"
        :style="cellStyle(foodPosition.x, foodPosition.y)"
        role="img"
        aria-label="Food"
      />

      <!-- Power Up -->
      <div
        v-if="powerUp"
        class="cell powerup"
        :style="cellStyle(powerUp.x, powerUp.y)"
        :data-type="powerUp.type || 'generic'"
        role="img"
        aria-label="Power up"
      />

      <!-- Snake -->
      <div
        v-for="(seg, i) in snakeBody"
        :key="`snake-${seg.x}-${seg.y}-${i}`"
        class="cell snake"
        :class="{ head: i === 0 }"
        :style="cellStyle(seg.x, seg.y)"
        aria-hidden="true"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { computed } from "vue";

const props = defineProps({
  boardSize: { type: Number, required: true },
  snakeBody: { type: Array, required: true },
  foodPosition: { type: Object, default: null },
  obstacles: { type: Array, default: () => [] },
  powerUp: { type: Object, default: null },
  theme: { type: String, default: "classic" },
  showGrid: { type: Boolean, default: true },
});

const gridStyle = computed(() => ({
  // --n es el número de celdas por lado
  "--n": props.boardSize,
}));

const cellStyle = (x, y) => ({
  // Mapea coordenadas 0..n-1 a tracks de grid 1..n
  gridColumnStart: x + 1,
  gridRowStart: y + 1,
});
</script>

<style scoped>
.board-wrap {
  display: grid;
  place-items: center;
  width: 100%;
}

/* El tablero es SIEMPRE cuadrado, responsivo y no cambia de tamaño
   cuando aparecen elementos. */
.game-board {
  width: min(92vmin, 720px);
  aspect-ratio: 1 / 1;
  display: grid;
  /* minmax(0, 1fr) evita que el contenido influya en el tamaño del track */
  grid-template-columns: repeat(var(--n), minmax(0, 1fr));
  grid-template-rows: repeat(var(--n), minmax(0, 1fr));
  gap: 0;
  background: #111;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

/* Rejilla (opcional) */
.game-board.show-grid {
  background-image: linear-gradient(
      #0000 0,
      #0000 calc(100% - 1px),
      rgba(255, 255, 255, 0.06) 0
    ),
    linear-gradient(
      90deg,
      #0000 0,
      #0000 calc(100% - 1px),
      rgba(255, 255, 255, 0.06) 0
    );
  background-size: calc(100% / var(--n)) 100%, 100% calc(100% / var(--n));
  background-repeat: repeat;
}

.cell {
  width: 100%;
  height: 100%;
  /* Evitar que borders/paddings afecten al layout */
  box-sizing: border-box;
}

/* Sprites por tema */
.theme--classic .snake:not(.head) {
  background: #2ecc71;
  border-radius: 6px;
}
.theme--classic .snake.head {
  background: radial-gradient(
    circle at 40% 40%,
    #a8ff78 0,
    #2ecc71 35%,
    #27ae60 100%
  );
  border-radius: 8px;
}
.theme--classic .food {
  background: radial-gradient(
    circle at 50% 50%,
    #ff6b6b 0,
    #ff3b3b 45%,
    #c72222 100%
  );
  border-radius: 50%;
}
.theme--classic .obstacle {
  background: #2b2b2b;
}
.theme--classic .powerup {
  background: conic-gradient(from 0deg, #ffd93d, #ff8c42, #ffd93d);
  border-radius: 25%;
}

/* Tema neon (por si quieres probar) */
.theme--neon .snake:not(.head) {
  background: #00e5ff;
  box-shadow: 0 0 8px #00e5ff;
  border-radius: 6px;
}
.theme--neon .snake.head {
  background: #00ffa2;
  box-shadow: 0 0 10px #00ffa2;
  border-radius: 8px;
}
.theme--neon .food {
  background: #ff007a;
  box-shadow: 0 0 8px #ff007a;
  border-radius: 50%;
}
.theme--neon .obstacle {
  background: #3a3a3a;
}
.theme--neon .powerup {
  background: #ffe066;
  box-shadow: 0 0 8px #ffe066;
  border-radius: 25%;
}
</style>
